/**
 * Route adapter components.
 *
 * Each component wraps an existing screen (MainMenu, App, OnlineGame,
 * Tutorial, AdminDashboard) and translates between react-router (URL +
 * location.state + useNavigate) and the props the underlying screen
 * already expects. The screens themselves stay almost untouched; this
 * file is a thin glue layer rather than a rewrite.
 *
 * Two-way data flow:
 *
 *   warm in-app navigation:
 *     form submit -> onStartGame(config) -> navigate(target, { state })
 *     -> destination route reads useLocation().state.config and mounts
 *
 *   cold deep-link / paste in new tab:
 *     destination route has no location.state -> reconstructs config
 *     from URL params (puzzle modes) or from saved-session (resume) or
 *     redirects to / (nothing to recover).
 */
import React, { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams, Navigate } from 'react-router-dom';

import MainMenu from './MainMenu';
import App, { clearLocalGame } from './App';
import Tutorial from './Tutorial';
import OnlineGame from './OnlineGame';
import AdminDashboard from './AdminDashboard';

import { trackEvent } from './telemetry';
import { clearSession, getSavedSession } from './savedSession';
import {
  parseSinglePuzzleSearchParams,
  serializeSinglePuzzleSearchParams,
  parseEndlessSearchParams,
  serializeEndlessSearchParams,
} from './puzzleParams';

// ── Scene <-> path mapping for the MainMenu scenes ───────────────────────────
//
// The menu still owns its rendering logic per-scene; routing just decides
// which scene's URL is current. When the menu calls onNavigateScene, we
// translate the scene id to a path and let react-router mount a fresh
// MainMenu with the new initialScene.

const MENU_SCENE_PATHS = {
  main: '/',
  vs_computer: '/vs-computer',
  local_multiplayer: '/local',
  online_multiplayer: '/online',
  puzzle_mode_select: '/puzzles',
  puzzle: '/puzzles/solve',                 // attemptMode resolved by the route
  endless_puzzle_config: '/puzzles/endless',
  pricing: '/pricing',
  leaderboard: '/leaderboard',
};

function scenePath(scene, attemptMode) {
  if (scene === 'puzzle') {
    if (attemptMode === 'llm') return '/puzzles/watch';
    if (attemptMode === 'endless') return '/puzzles/endless';
    return '/puzzles/solve';
  }
  return MENU_SCENE_PATHS[scene] || '/';
}

// ── Menu route ───────────────────────────────────────────────────────────────

function fireMenuTelemetry(config) {
  const aiProvider = config.gameMode === 'vs_computer'
    ? (config.p1 !== 'Human' ? config.p1 : config.p2)
    : null;
  trackEvent('menu_event', {
    mode: config.gameMode,
    ai_provider: aiProvider,
    ai_model: config.modelName || null,
    rows: config.rows,
    cols: config.cols,
    llm_plan_mode: config.llmPlanMode || null,
    time_control: config.timeControl || null,
  });
}

function routeForStartGame(config) {
  // Decide the URL the config should land on, plus the search params (puzzle
  // modes serialize their knobs so the URL is shareable).
  if (config.gameMode === 'online') {
    return { path: `/online/${config.gameId}`, search: '' };
  }
  if (config.gameMode === 'puzzle') {
    const attempt = config.puzzleAttemptMode;
    if (attempt === 'endless') {
      const params = serializeEndlessSearchParams({
        perType: endlessFormFromPlayConfig(config),
        runLengthId: config.endless?.runLengthId,
      });
      return { path: '/puzzles/endless/play', search: `?${params.toString()}` };
    }
    const params = serializeSinglePuzzleSearchParams({
      puzzle_type:  config.puzzle?.puzzle_type,
      num_jumps:    config.puzzle?.num_jumps,
      noise:        config.puzzle?.noise,
      max_jump_len: config.puzzle?.max_jump_len,
      player:       config.puzzle?.player,
      seed:         config.puzzle?.seed,
      llmProvider:  config.llmProvider,
      llmModel:     config.llmModel,
    });
    const mode = attempt === 'llm' ? 'watch' : 'solve';
    return { path: `/puzzles/${mode}/play`, search: `?${params.toString()}` };
  }
  return { path: '/play', search: '' };
}

function endlessFormFromPlayConfig(config) {
  // Re-shape the runtime config.endless payload back into the form shape
  // the serializer expects (perType.{win,threat,...}{enabled,minN,maxN,...}).
  const out = { win: {}, threat: {}, passthrough: {}, denial: {} };
  const enabled = new Set(config.endless?.enabledTypes || []);
  const perCfg = config.endless?.perTypeConfig || {};
  for (const t of Object.keys(out)) {
    const c = perCfg[t] || {};
    out[t] = {
      enabled: enabled.has(t),
      minN: c.minN,
      maxN: c.maxN,
      noise: c.noise,
      maxJumpLen: c.maxJumpLen,
    };
  }
  return out;
}

export function MenuRoute({ scene, attemptMode = null }) {
  const navigate = useNavigate();

  const handleNavigateScene = useCallback((newScene, opts = {}) => {
    navigate(scenePath(newScene, opts.attemptMode));
  }, [navigate]);

  const handleStartGame = useCallback((config) => {
    fireMenuTelemetry(config);
    // Clear prior local-game state for fresh starts (online doesn't touch it).
    if (config.gameMode !== 'online') {
      clearSession();
      clearLocalGame();
    }
    const { path, search } = routeForStartGame(config);
    navigate(`${path}${search}`, { state: { config } });
  }, [navigate]);

  const handleStartTutorial = useCallback((type) => {
    const resolved = type || 'full';
    trackEvent('menu_event', { mode: 'tutorial', tutorial_type: resolved });
    navigate(resolved === 'genesis' ? '/tutorial/quickstart' : '/tutorial/full');
  }, [navigate]);

  const handleResumeGame = useCallback(() => {
    const saved = getSavedSession();
    if (saved?.config) {
      navigate('/play', { state: { config: saved.config, restore: true } });
    }
  }, [navigate]);

  const handleDiscardSession = useCallback(() => {
    clearSession();
  }, []);

  const handleRejoinOnlineGame = useCallback((pending) => {
    if (!pending?.gameId) return;
    const cfg = {
      gameMode: 'online',
      gameId: pending.gameId,
      opponent: { displayName: pending.opponentName },
    };
    navigate(`/online/${pending.gameId}`, { state: { config: cfg } });
  }, [navigate]);

  // Re-mount on (scene, attemptMode) change so MainMenu's initialScene /
  // initialAttemptMode useState seeds re-run with the URL's intent.
  // `/puzzles/solve` and `/puzzles/watch` both map to scene='puzzle' and
  // differ only by attemptMode, so keying on scene alone would skip the
  // remount when toggling between them.
  return (
    <MainMenu
      key={`${scene}:${attemptMode || ''}`}
      initialScene={scene}
      initialAttemptMode={attemptMode}
      onStartGame={handleStartGame}
      onStartTutorial={handleStartTutorial}
      onResumeGame={handleResumeGame}
      onDiscardSession={handleDiscardSession}
      onRejoinOnlineGame={handleRejoinOnlineGame}
      savedSession={getSavedSession()}
      onNavigateScene={handleNavigateScene}
    />
  );
}

// ── Tutorial routes ──────────────────────────────────────────────────────────

export function TutorialRoute({ type }) {
  const navigate = useNavigate();
  return <Tutorial tutorialType={type} onExit={() => navigate('/')} />;
}

// ── Local-game play route ────────────────────────────────────────────────────
//
// /play handles the local-multiplayer + vs-computer + (legacy non-puzzle)
// case. On warm navigation it reads config from location.state. On cold
// paste it falls back to a saved session if any, otherwise bounces home.

export function PlayRoute() {
  const location = useLocation();
  const navigate = useNavigate();

  const stateConfig = location.state?.config;
  const restoreFromState = location.state?.restore === true;
  const savedSession = useMemo(() => (stateConfig ? null : getSavedSession()), [stateConfig]);
  const config = stateConfig || savedSession?.config || null;
  const restoreSessionData = restoreFromState || !stateConfig
    ? (savedSession?.sessionData ?? null)
    : null;

  useEffect(() => {
    if (!config) navigate('/', { replace: true });
  }, [config, navigate]);

  if (!config) return null;
  return (
    <App
      config={config}
      onExit={() => navigate('/')}
      restoreSessionData={restoreSessionData}
    />
  );
}

// ── Puzzle play routes ───────────────────────────────────────────────────────
//
// /puzzles/solve/play, /puzzles/watch/play, /puzzles/endless/play all share
// PuzzlePlayRoute and just pass a `mode` prop. On a cold load (state.config
// is absent) the route rebuilds the runtime config from URL search params.
// For Watch mode the API key is read from sessionStorage at play time;
// puzzleParams deliberately never serialises it.

const DEFAULT_PER_TYPE = {
  win:         { enabled: true, minN: 1, maxN: 4, noise: 10, maxJumpLen: 3 },
  threat:      { enabled: true, minN: 2, maxN: 4 },
  passthrough: { enabled: true, minN: 2, maxN: 4 },
  denial:      { enabled: true, minN: 4, maxN: 6 },
};

const SINGLE_PUZZLE_TYPE_DEFAULTS = {
  win:         { num_jumps: 1, noise: 10, max_jump_len: 3 },
  threat:      { num_jumps: 2, noise: 0,  max_jump_len: 3 },
  passthrough: { num_jumps: 2, noise: 0,  max_jump_len: 3 },
  denial:      { num_jumps: 4, noise: 0,  max_jump_len: 3 },
};

function buildSinglePuzzleConfig(parsed, attemptMode) {
  const type = parsed.puzzle_type || 'win';
  const def = SINGLE_PUZZLE_TYPE_DEFAULTS[type] || SINGLE_PUZZLE_TYPE_DEFAULTS.win;
  const llmFields = attemptMode === 'llm' && parsed.llmProvider && parsed.llmModel
    ? loadLlmKeyFor(parsed.llmProvider, parsed.llmModel)
    : null;
  return {
    p1: attemptMode === 'llm' && (parsed.player ?? 1) === 1 && parsed.llmProvider
        ? parsed.llmProvider : 'Human',
    p2: attemptMode === 'llm' && (parsed.player ?? 1) === 2 && parsed.llmProvider
        ? parsed.llmProvider : 'Human',
    modelName: attemptMode === 'llm' ? (parsed.llmModel || null) : null,
    rows: 21,
    cols: 15,
    gameMode: 'puzzle',
    puzzleAttemptMode: attemptMode === 'llm' ? 'llm' : 'manual',
    ...(llmFields || {}),
    puzzle: {
      puzzle_type: type,
      num_jumps:   parsed.num_jumps ?? def.num_jumps,
      player:      parsed.player ?? 1,
      noise:       type === 'win' ? (parsed.noise ?? def.noise) : 0,
      max_jump_len: parsed.max_jump_len ?? def.max_jump_len,
      ...(parsed.seed != null ? { seed: parsed.seed } : {}),
    },
  };
}

function loadLlmKeyFor(provider /* , model */) {
  // sessionStorage shape mirrors what PuzzleConfig writes: { [provider]: key }
  try {
    const raw = sessionStorage.getItem('phutball_api_keys');
    const keys = raw ? JSON.parse(raw) : {};
    const apiKey = keys[provider];
    return apiKey ? { llmProvider: provider, llmApiKey: apiKey } : { llmProvider: provider };
  } catch {
    return { llmProvider: provider };
  }
}

function buildEndlessConfig(parsedEndless) {
  // Translate the form/URL shape (perType.{enabled,minN,maxN,noise,maxJumpLen})
  // back to the runtime payload App.js expects.
  const enabledTypes = [];
  const perTypeConfig = {};
  for (const t of Object.keys(parsedEndless.perType)) {
    const c = parsedEndless.perType[t];
    if (c.enabled) enabledTypes.push(t);
    perTypeConfig[t] = {
      minN: c.minN,
      maxN: c.maxN,
      noise: t === 'win' ? (c.noise ?? 10) : 0,
      maxJumpLen: t === 'win' ? (c.maxJumpLen ?? 3) : 3,
    };
  }
  const RUN_LENGTH_CAPS = { endless: 0, twelve: 12, twentyfour: 24 };
  const runLengthId = parsedEndless.runLengthId || 'endless';
  return {
    p1: 'Human', p2: 'Human',
    modelName: null,
    rows: 21, cols: 15,
    gameMode: 'puzzle',
    puzzleAttemptMode: 'endless',
    endless: {
      enabledTypes,
      runLengthId,
      cap: RUN_LENGTH_CAPS[runLengthId] ?? 0,
      perTypeConfig,
    },
  };
}

export function PuzzlePlayRoute({ mode /* 'solve' | 'watch' | 'endless' */ }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Memoize so cold-load reconstruction doesn't hand App a fresh config
  // object on every render. App's usePuzzleQueue uses nested config fields
  // (endless.enabledTypes, endless.perTypeConfig) as effect deps, and
  // churning their identity causes the endless topup loop to recreate
  // mid-fetch and the next-puzzle popper to stall.
  const stateConfig = location.state?.config || null;
  const searchString = searchParams.toString();
  const config = useMemo(() => {
    if (stateConfig) return stateConfig;
    if (mode === 'endless') {
      const parsed = parseEndlessSearchParams(searchParams, DEFAULT_PER_TYPE);
      // If types= is missing entirely, default to all enabled; the parser
      // returns enabledTypes=null in that case and per-type entries
      // default-enabled, so buildEndlessConfig already does the right thing.
      return buildEndlessConfig(parsed);
    }
    const parsed = parseSinglePuzzleSearchParams(searchParams);
    const attemptMode = mode === 'watch' ? 'llm' : 'manual';
    return buildSinglePuzzleConfig(parsed, attemptMode);
    // searchString stands in for searchParams (URLSearchParams identity is
    // not guaranteed stable across renders even when the URL hasn't changed).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateConfig, mode, searchString]);

  return <App config={config} onExit={() => navigate('/')} />;
}

// ── Online game route ────────────────────────────────────────────────────────

export function OnlineGameRoute() {
  const { gameId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const config = useMemo(() => {
    if (location.state?.config && location.state.config.gameId === gameId) {
      return location.state.config;
    }
    // Cold deep-link: reconstruct a minimal online config from the URL alone.
    // OnlineGame uses gameId as the source of truth; opponent metadata, if any,
    // arrives over the wire after connect.
    return { gameMode: 'online', gameId };
  }, [gameId, location.state]);

  if (!gameId) {
    return <Navigate to="/online" replace />;
  }
  return <OnlineGame gameId={config.gameId} onExit={() => navigate('/')} />;
}

// ── Admin route ──────────────────────────────────────────────────────────────

export function AdminRoute() {
  const navigate = useNavigate();
  return <AdminDashboard onExit={() => navigate('/')} />;
}

// Backwards-compat: old bookmarks were ?admin=1 / #admin off the root.
// Mount this at the root so a legacy URL still lands on /admin.
export function AdminLegacyRedirect({ children }) {
  const navigate = useNavigate();
  useEffect(() => {
    const search = window.location.search || '';
    const hash = window.location.hash || '';
    if (hash === '#admin' || /(?:^|[?&])admin(?:=|&|$)/.test(search)) {
      navigate('/admin', { replace: true });
    }
  }, [navigate]);
  return children;
}

// ── Not-found fallback ───────────────────────────────────────────────────────

export function NotFoundRoute() {
  return <Navigate to="/" replace />;
}
